'use client';
import SplashButton from '@/components/SplashButton';
import { ModalVideoPlayer } from '@/components/ModalVideoPlayer';
import React, { useEffect, useState } from 'react';

// ViewTransition is experimental and may not be available in React 19
// Create a fallback component that works whether ViewTransition exists or not
const ViewTransition = (React as any).unstable_ViewTransition || (({ children }: { children: React.ReactNode; name?: string }) => <>{children}</>);
import { PrismicRichText } from '@prismicio/react';
import { asText } from '@prismicio/helpers';
import { VideoDocument } from '@/prismicio-types';
import Image from 'next/image';
import TransitionLink from '@/components/TransitionLink';
import { formatPrismicDate } from '@/lib/date';

interface VideoDetailProps {
  video: VideoDocument;
  language?: string;
  minutesLabel?: string;
  backLabel?: string;
  watchVideoLabel?: string;
  clickToSeeLabel?: string;
  speakersLabel?: string;
}

const VideoDetail: React.FC<VideoDetailProps> = ({
  video,
  minutesLabel,
  backLabel,
  watchVideoLabel,
  clickToSeeLabel,
  speakersLabel,
  language,
}) => {
  const [showButton, setShowButton] = useState(false);
  const [speakersOpen, setSpeakersOpen] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setShowButton(true), 2000);
    return () => clearTimeout(timeout);
  }, []);

  // Map speakers from Prismic group field
  const speakers =
    video.data.speakers?.map((s) => ({
      name: s.name,
      position: s.position,
      company: s.company,
    })) || [];

  return (
    <div className="min-h-screen bg-[#111111] pb-16">
      <article itemScope itemType="https://schema.org/VideoObject">
        <meta itemProp="name" content={asText(video.data.title) || ''} />
        <meta itemProp="description" content={video.data.teaser || ''} />
        <meta itemProp="thumbnailUrl" content={video.data.image.url || ''} />
        <meta itemProp="uploadDate" content={video.first_publication_date || ''} />
        <meta
          itemProp="duration"
          content={video.data.duration ? `PT${parseInt(video.data.duration)}M` : ''}
        />
        {video.data.youtube_id && (
          <meta
            itemProp="embedUrl"
            content={`https://www.youtube.com/embed/${video.data.youtube_id}`}
          />
        )}

        <div className="relative aspect-video h-[440px] w-full overflow-hidden sm:h-auto sm:max-h-[60vh]">
          <ViewTransition name={`video-image-${video.uid}`}>
            <div className="absolute inset-0 h-full w-full">
              <Image
                src={video.data.image.url || ''}
                alt={video.data.image.alt || asText(video.data.title) || 'Video background'}
                fill
                className="absolute inset-0 object-cover object-bottom"
                sizes="100vw"
                priority={true}
                itemProp="thumbnail"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent" />
            </div>
          </ViewTransition>
          <div className="absolute inset-0 flex flex-col items-center justify-end align-middle">
            <div className="container">
              <div className="mx-auto flex max-w-6xl flex-col items-center bg-transparent py-8 sm:items-start">
                <ViewTransition name={`video-title-${video.uid}`}>
                  <h2
                    className="mb-6 max-w-5xl text-center text-3xl font-medium leading-tight text-white sm:text-left sm:text-5xl sm:font-light"
                    itemProp="name"
                  >
                    <PrismicRichText field={video.data.title} />
                  </h2>
                </ViewTransition>
                <div
                  className={`transition-opacity duration-700 ${showButton ? 'opacity-100' : 'opacity-0'}`}
                >
                  <ViewTransition name={`video-watch-${video.uid}`}>
                    {video.data.youtube_id && (
                      <ModalVideoPlayer
                        videoId={video.data.youtube_id}
                        videoTitle={asText(video.data.title) || ''}
                        videoLocation={'Video Detail'}
                      >
                        <div itemProp="potentialAction">
                          <SplashButton
                            text={watchVideoLabel || 'Watch Video'}
                            icon="video"
                            color="red"
                            size="lg"
                            className="mt-2 min-w-[240px]"
                            url="#"
                            preventNavigation={true}
                          />
                        </div>
                      </ModalVideoPlayer>
                    )}
                  </ViewTransition>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Speakers on top, collapsible */}
        <div className="container">
          <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 gap-2 md:grid-cols-3 md:gap-16">
            <div className="mb-2 md:hidden">
              <button
                className="mb-4 flex w-full items-center text-left text-base font-semibold text-yellow focus:outline-none sm:text-lg"
                onClick={() => setSpeakersOpen((open) => !open)}
                aria-expanded={speakersOpen}
              >
                <span className="uppercase">
                  <span className="inline sm:hidden">{clickToSeeLabel} </span>
                  {speakersLabel}
                </span>
                <svg
                  className={`ml-2 h-5 w-5 transition-transform duration-200 ${speakersOpen ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
              {speakersOpen && (
                <ul className="mb-2 space-y-4 border-b border-dashed border-[#444] pb-6">
                  {speakers.map((speaker, idx) => (
                    <li key={idx} itemScope itemType="https://schema.org/Person">
                      <div className="font-medium text-white" itemProp="name">
                        {speaker.name}
                      </div>
                      {(speaker.position || speaker.company) && (
                        <div className="text-sm text-white/70">
                          <span itemProp="jobTitle">{speaker.position}</span>
                          {speaker.position && speaker.company && ', '}
                          <span
                            itemProp="worksFor"
                            itemScope
                            itemType="https://schema.org/Organization"
                          >
                            <span itemProp="name">{speaker.company}</span>
                          </span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Desktop: Speakers right, description left */}
            <div className="order-2 md:order-1 md:col-span-2">
              <div
                className="prose blog-content prose-invert text-white/70 sm:text-lg"
                itemProp="description"
              >
                <PrismicRichText field={video.data.description} />
              </div>
              <div className="mt-8 flex flex-col items-start justify-start gap-16 text-base italic text-white/70 sm:mt-16 sm:flex-row-reverse sm:items-center sm:justify-between sm:gap-0 sm:gap-4 sm:gap-8 sm:text-sm">
                <div className="flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-8">
                  <span className="flex items-center gap-2">
                    <svg
                      className="inline-block h-4 w-4 text-white/60"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <time
                      itemProp="datePublished"
                      dateTime={video.data.publish_date || video.first_publication_date || ''}
                    >
                      {formatPrismicDate(video.data.publish_date) || 'N/A'}
                    </time>
                  </span>
                  <span className="flex items-center gap-2">
                    <svg
                      className="inline-block h-4 w-4 text-white/60"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l3 3" />
                    </svg>
                    <span
                      itemProp="duration"
                      content={video.data.duration ? `PT${parseInt(video.data.duration)}M` : ''}
                    >
                      {video.data.duration || 'N/A'} {minutesLabel || 'minutes'}
                    </span>
                  </span>
                </div>
                <div className="flex justify-center sm:justify-start">
                  <TransitionLink
                    href={`/${language || 'en-us'}/videos`}
                    className="group flex items-center gap-1 font-bold text-blue-300 underline underline-offset-2 transition-colors hover:text-blue-400"
                    aria-label="Back to all videos"
                    scroll={false}
                  >
                    <svg
                      className="inline-block h-4 w-4 text-blue-300 transition-transform duration-200 group-hover:-translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    {backLabel}
                  </TransitionLink>
                </div>
              </div>
            </div>
            <div className="order-1 hidden md:order-2 md:col-span-1 md:block">
              <h5 className="mb-4 text-lg font-semibold uppercase text-yellow">
                {speakersLabel || 'Speakers'}
              </h5>
              <ul className="space-y-4">
                {speakers.map((speaker, idx) => (
                  <li key={idx} itemScope itemType="https://schema.org/Person">
                    <div className="font-medium text-white" itemProp="name">
                      {speaker.name}
                    </div>
                    {(speaker.position || speaker.company) && (
                      <div className="text-sm text-white/70">
                        <span itemProp="jobTitle">{speaker.position}</span>
                        {speaker.position && speaker.company && ', '}
                        <span
                          itemProp="worksFor"
                          itemScope
                          itemType="https://schema.org/Organization"
                        >
                          <span itemProp="name">{speaker.company}</span>
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default VideoDetail;
